import { useTablePaginationManager } from "@powerledger/ui-component-lib";
import { subYears } from "date-fns";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ForwardTradeWithdrawCustomProps } from "@/app/components/forward-trade-withdraw-modal/forward-trade-withdraw-modal.types";
import {
  appendErcotVintageHalvesToMretsVintagesFilterValues,
  defaultFilter,
  mapStackedFilterStateToFilterState,
} from "@/app/components/page-header/filter";
import { FilterState } from "@/app/components/page-header/filter/filter.types";
import { TransactionSummary } from "@/app/components/pdf-components/documents/transaction-summary";
import { TransactionSummaryData } from "@/app/components/pdf-components/documents/transaction-summary/transaction-summary.types";
import { useAppContext } from "@/app/context";
import { useAllRecOptions } from "@/app/hooks/use-all-rec-options";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useHasPermissions } from "@/app/hooks/use-has-permission";
import { UserFeatures } from "@/app/hooks/use-okta-profile-features";
import { useRecOptions } from "@/app/hooks/use-rec-options";
import { getDatePart } from "@/app/lib/date-helpers";
import { formatAttributes, ReturnTypeFormatAttributes } from "@/app/lib/format-attributes";
import { ModalContext } from "@/app/modal-provider/modal-context";
import {
  RecAttributes,
  SimpleAccount,
  SortOrderInput,
  TradeContract,
  TradeContractStatus,
  TradePosition,
  useTradeContractHistoryQuery,
  useTradeContractTransactionSummaryLazyQuery,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { SupportedDocumentType } from "../components/download-report-buttons";
import { getForwardTradeColumns } from "./forward-trade-columns";
import {
  AssignRecsProps,
  PartialAccountInfo,
  TradeContractHistoryTableData,
  UseForwardTradeHistoryFn,
} from "./forward-trade-history.types";
import { downloadTransactionSummaryReport } from "./helpers";

export const useForwardTradeHistory: UseForwardTradeHistoryFn = () => {
  const { hasFeaturePermission } = useHasPermissions({ skipQuery: true });
  const [showAllAttributes, setShowAllAttributes] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userSelections } = useAppContext();
  const [activeRegistryCode, setActiveRegistryCode] = useState<AggregatedRegistryCode>(ANY_REGISTRY_CODE);
  const [activeFilters, setActiveFilters] = useState<FilterState>(defaultFilter);
  const { showForwardTradeRecsAssignModal, showForwardTradeRecsWithdrawModal, showForwardTradeDetailsModal } =
    useContext(ModalContext);
  const { toggles, loading: featureTogglesLoading } = useAllFeatureToggleValues();
  const { enableCrossRegistryForwardTrading } = toggles;

  const { pageInfo, offset, fetchData, setTotalItems, sort, resetPage } = useTablePaginationManager<
    TradeContract,
    SortOrderInput[]
  >({});

  const { recOptions, loading: recOptionsLoading } = useRecOptions({
    registryCode: enableCrossRegistryForwardTrading
      ? activeRegistryCode
      : (userSelections?.registry?.registryCode as AggregatedRegistryCode),
  });

  const { mergedRecOptions, recOptionsByRegistryCode, loading: allRecOptionsLoading } = useAllRecOptions();

  const { data, loading, refetch } = useTradeContractHistoryQuery({
    fetchPolicy: "cache-and-network",
    skip: featureTogglesLoading,
    variables: {
      input: {
        paginationInput: {
          offset,
          limit: pageInfo.pageSize,
        },
        sortOrderInputs: sort,
        where: {
          recAttributes: {
            vintages: enableCrossRegistryForwardTrading
              ? appendErcotVintageHalvesToMretsVintagesFilterValues(activeFilters.vintages, activeRegistryCode)
              : activeFilters.vintages,
            eligibilities: activeFilters.eligibilities,
            locations: activeFilters.locations,
            fuelSources: activeFilters.fuelSources,
            commencementDateGte: activeFilters.cod?.length
              ? getDatePart(subYears(new Date(), +activeFilters.cod?.[0]))
              : undefined,
            certifications: activeFilters.certifications,
            certificateTypes: activeFilters.certificateType,
          },
          statuses: activeFilters.statuses as TradeContractStatus[],
          tradeSettlementDate: activeFilters.selectedDate[0],
          registryCode: enableCrossRegistryForwardTrading ? activeRegistryCode : userSelections?.registry?.registryCode,
        },
      },
    },
    onCompleted(data) {
      setTotalItems(data.tradeContractHistory.offsetInfo.total ?? 0);
    },
  });

  const [transactionSummaryQuery] = useTradeContractTransactionSummaryLazyQuery({
    fetchPolicy: "no-cache",
  });

  //added new attribute types on "formatAttributes"
  const tableData = useMemo(() => {
    const tradeContractHistoryTableData: TradeContractHistoryTableData[] = [];

    if (!data?.tradeContractHistory.tradeContracts) return tradeContractHistoryTableData;
    for (const tradeContract of data.tradeContractHistory.tradeContracts) {
      const attributes = tradeContract.attributes as RecAttributes;
      const account = tradeContract.account as PartialAccountInfo;
      const counterAccount = tradeContract.counterAccount as SimpleAccount;
      const options = enableCrossRegistryForwardTrading
        ? recOptionsByRegistryCode[tradeContract.registryCode as TraceXSupportedRegistries] ?? mergedRecOptions
        : recOptions;
      const formattedAttributes = options
        ? formatAttributes({
            attributes,
            /** For forward trades always display "Any" in case of attribute is not specified, regardless of Bid or Ask */
            position: TradePosition.Bid,
            options,
          })
        : ({} as ReturnTypeFormatAttributes);
      tradeContractHistoryTableData.push({
        ...tradeContract,
        attributes,
        account,
        counterAccount,
        formattedAttributes: {
          ...attributes,
          vintages: formattedAttributes.vintages,
          eligibilities: formattedAttributes.eligibilities,
          certifications: formattedAttributes.certifications,
          fuelSources: formattedAttributes.fuelSources,
          locations: formattedAttributes.locations,
          projects: formattedAttributes.projects,
          certificationsAndEligibilities: formattedAttributes.certificationsAndEligibilities,
          certificateTypes: formattedAttributes.certificateTypes,
        },
      });
    }
    return tradeContractHistoryTableData;
  }, [
    recOptions,
    data?.tradeContractHistory.tradeContracts,
    enableCrossRegistryForwardTrading,
    mergedRecOptions,
    recOptionsByRegistryCode,
  ]);

  const reloadTradeContractHistory = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRequestDateChange = useCallback(
    (tradeContract: TradeContractHistoryTableData) => {
      navigate(`/orders/forward-trade/request-date-change/${tradeContract.id}`);
    },
    [navigate],
  );

  const handleAssignRecs = useCallback(
    ({
      tradeContractNumber,
      tradeContractId,
      askTradeVolume,
      tradeAttributes,
      status,
      assetAssignmentStatus,
      tradeContractRegistryCode,
    }: AssignRecsProps) => {
      showForwardTradeRecsAssignModal({
        tradeContractNumber,
        tradeContractRegistryCode,
        tradeContractId,
        askTradeVolume,
        tradeAttributes,
        status,
        assetAssignmentStatus,
      });
    },
    [showForwardTradeRecsAssignModal],
  );

  const handleWithdrawRecs = useCallback(
    ({ tradeID, registryCode }: ForwardTradeWithdrawCustomProps) => {
      showForwardTradeRecsWithdrawModal({
        tradeID,
        registryCode,
      });
    },
    [showForwardTradeRecsWithdrawModal],
  );
  // Workaround stop react-pdf rendering the pdf when the button renders and
  // only render the pdf when the button is clicked
  const downloadTransactionSummary = useCallback(
    async (
      type: SupportedDocumentType,
      {
        orderId,
        orderNumber,
        rowRegistryCode,
      }: {
        orderId: string;
        orderNumber: string;
        rowRegistryCode: AggregatedRegistryCode;
      },
    ) => {
      return downloadTransactionSummaryReport(
        type,
        orderId,
        orderNumber,
        //once enableCrossRegistryForwardTrading is removed, remove recOptions props
        {
          recOptions,
          recOptionsByRegistryCode,
          mergedRecOptions,
        },
        transactionSummaryQuery,
        rowRegistryCode,
        enableCrossRegistryForwardTrading,
        (transactionSummaryData: TransactionSummaryData) => (
          <TransactionSummary
            title={"Marketplace Forward Contract Summary"}
            description={
              "This Document is a summary of your Forward Contract for the purchase or sale of Product in accordance with TraceX Rule 504(a)."
            }
            tradeType="Forward"
            transactionSummary={transactionSummaryData}
            rowRegistryCode={rowRegistryCode}
          />
        ),
      );
    },
    [
      recOptions,
      transactionSummaryQuery,
      enableCrossRegistryForwardTrading,
      mergedRecOptions,
      recOptionsByRegistryCode,
    ],
  );

  const tableColumns = useMemo(
    () =>
      getForwardTradeColumns(
        t,
        {
          handleAssignRecs,
          handleWithdrawRecs,
          handleRequestDateChange,
          showForwardTradeDetailsModal,
          downloadTransactionSummary,
        },
        toggles,
      ),
    [
      t,
      handleAssignRecs,
      handleWithdrawRecs,
      handleRequestDateChange,
      showForwardTradeDetailsModal,
      downloadTransactionSummary,
      toggles,
    ],
  );

  const handleFilterChange = useCallback(
    (arg) => {
      setActiveFilters((activeFilters) => ({ ...activeFilters, ...mapStackedFilterStateToFilterState(arg) }));
      resetPage();
    },
    [resetPage],
  );

  return {
    forwardTradeEnabled: hasFeaturePermission([UserFeatures.FORWARD_TRADE_ENABLED]),
    tableData,
    loading: loading || recOptionsLoading || featureTogglesLoading || allRecOptionsLoading,
    fetchData,
    pageInfo,
    reloadTradeContractHistory,
    tableColumns,
    showAllAttributes,
    setShowAllAttributes,
    handleFilterChange,
    activeRegistryCode: enableCrossRegistryForwardTrading
      ? activeRegistryCode
      : (userSelections?.registry?.registryCode as AggregatedRegistryCode),
    setActiveRegistryCode,
    enableCrossRegistryForwardTrading,
  };
};
